import React, { useState } from "react";
import RoboticsShell from "./RoboticsShell";
import { api } from "../../lib/api";

/**
 * Schools & Partners — institutional contact page. POSTs to
 * /api/robotics/school-inquiry which stores the enquiry for manual
 * follow-up.
 */
export default function RoboticsSchools() {
  const [form, setForm] = useState({
    school_name: "", contact_name: "", role: "", email: "",
    country: "", students: "", message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.school_name || !form.email || !form.contact_name) {
      setStatus({ state: "err", msg: "School name, contact name and email are required." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/robotics/school-inquiry", form);
      setStatus({ state: "ok", msg: "Received. Our schools team will reply within two business days." });
      setForm({ school_name: "", contact_name: "", role: "", email: "", country: "", students: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again later.";
      setStatus({ state: "err", msg: String(detail) });
    }
  }

  return (
    <RoboticsShell>
      <section className="rbx-page" data-testid="rbx-page-schools">
        <span className="rbx-eyebrow">Section · Schools &amp; partners</span>
        <h1 className="rbx-page__title" data-testid="rbx-schools-title">
          For schools, districts &amp; makerspaces.
        </h1>
        <p className="rbx-page__lede">
          Institutional pricing for 20+ seats. Bulk kit sourcing available at
          cost. Curriculum can be delivered as a full 3-year path or as a
          single term for elective slots. Sponsorship match for Girls@TEC
          scholarships.
        </p>

        <div style={{ display: "grid", gap: 24, gridTemplateColumns: "1.1fr 1fr", alignItems: "start" }}
             className="rbx-schools-layout">
          <form className="rbx-form" onSubmit={onSubmit} data-testid="rbx-schools-form" style={{ maxWidth: 620 }}>
            <div className="rbx-form__row">
              <div>
                <label className="rbx-label" htmlFor="rbx-schools-school">School name</label>
                <input id="rbx-schools-school" className="rbx-input" value={form.school_name}
                       onChange={(e) => update("school_name", e.target.value)} data-testid="rbx-schools-school"/>
              </div>
              <div>
                <label className="rbx-label" htmlFor="rbx-schools-country">Country</label>
                <input id="rbx-schools-country" className="rbx-input" value={form.country}
                       onChange={(e) => update("country", e.target.value)} data-testid="rbx-schools-country"/>
              </div>
            </div>
            <div className="rbx-form__row">
              <div>
                <label className="rbx-label" htmlFor="rbx-schools-contact">Your name</label>
                <input id="rbx-schools-contact" className="rbx-input" value={form.contact_name}
                       onChange={(e) => update("contact_name", e.target.value)} data-testid="rbx-schools-contact"/>
              </div>
              <div>
                <label className="rbx-label" htmlFor="rbx-schools-role">Your role</label>
                <input id="rbx-schools-role" className="rbx-input" value={form.role}
                       onChange={(e) => update("role", e.target.value)} data-testid="rbx-schools-role"
                       placeholder="Head of STEM, Principal, …"/>
              </div>
            </div>
            <div className="rbx-form__row">
              <div>
                <label className="rbx-label" htmlFor="rbx-schools-email">Email</label>
                <input id="rbx-schools-email" className="rbx-input" type="email" value={form.email}
                       onChange={(e) => update("email", e.target.value)} data-testid="rbx-schools-email"/>
              </div>
              <div>
                <label className="rbx-label" htmlFor="rbx-schools-students">Approx. students</label>
                <input id="rbx-schools-students" className="rbx-input" type="number" value={form.students}
                       onChange={(e) => update("students", e.target.value)} data-testid="rbx-schools-students"
                       placeholder="e.g. 60"/>
              </div>
            </div>
            <div>
              <label className="rbx-label" htmlFor="rbx-schools-message">Anything specific?</label>
              <textarea id="rbx-schools-message" className="rbx-textarea" value={form.message}
                        onChange={(e) => update("message", e.target.value)} data-testid="rbx-schools-message"
                        placeholder="Timelines, existing programmes, kit budget, sponsorship match interest…"/>
            </div>
            <div>
              <button type="submit" className="rbx-btn rbx-btn--primary"
                      disabled={status.state === "loading"} data-testid="rbx-schools-submit">
                {status.state === "loading" ? "Sending…" : "Send enquiry"}
              </button>
            </div>
            {status.msg ? (
              <p className={`rbx-form__msg ${status.state === "ok" ? "rbx-form__msg--ok" : "rbx-form__msg--err"}`}
                 data-testid="rbx-schools-msg">
                {status.msg}
              </p>
            ) : null}
          </form>

          <aside className="rbx-card" data-testid="rbx-schools-pricing" style={{ maxWidth: 460 }}>
            <div className="rbx-band__num">BULK PRICING · INDICATIVE</div>
            <ul style={{ listStyle: "none", padding: 0, margin: "10px 0", lineHeight: 1.7, color: "var(--rbx-ink-mute)" }}>
              <li><strong style={{ color: "var(--rbx-gold)" }}>20–60 seats</strong> · US $12 / student / term</li>
              <li><strong style={{ color: "var(--rbx-gold)" }}>60–150 seats</strong> · US $9 / student / term</li>
              <li><strong style={{ color: "var(--rbx-gold)" }}>150+ seats</strong> · custom, includes teacher training</li>
              <li><strong style={{ color: "var(--rbx-coral)" }}>Girls@TEC match</strong> · we match your sponsored seats 1:1</li>
              <li><strong style={{ color: "var(--rbx-gold)" }}>Kit sourcing</strong> · at cost + logistics, no markup</li>
            </ul>
            <p style={{ fontSize: 12, color: "var(--rbx-ink-dim)", marginTop: 6 }}>
              Indicative only. Final quote issued after enquiry review.
            </p>
          </aside>
        </div>
      </section>
    </RoboticsShell>
  );
}
